/**
 * Locale Store - Laumeris
 * Gère la langue de l'application avec auto-détection
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type LocaleCode = 'fr' | 'en' | 'de' | 'es' | 'zh'

export interface LocaleOption {
  code: LocaleCode
  name: string
  nativeName: string
  flag: string
}

export const AVAILABLE_LOCALES: LocaleOption[] = [
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' }
]

export const useLocaleStore = defineStore('locale', () => {
  // State
  const currentLocale = ref<LocaleCode>('fr')

  // Computed
  const currentLocaleOption = computed(() => 
    AVAILABLE_LOCALES.find(l => l.code === currentLocale.value) || AVAILABLE_LOCALES[0]
  )

  // Detect system language
  const detectSystemLocale = (): LocaleCode => {
    if (typeof window === 'undefined') return 'fr'
    
    const browserLang = navigator.language || (navigator as any).userLanguage || 'fr'
    const langCode = browserLang.split('-')[0].toLowerCase()
    
    // Map to our supported locales
    const localeMap: Record<string, LocaleCode> = {
      'fr': 'fr',
      'en': 'en',
      'de': 'de',
      'es': 'es',
      'zh': 'zh'
    }
    
    return localeMap[langCode] || 'fr'
  }

  // Set locale
  const setLocale = (locale: LocaleCode) => {
    currentLocale.value = locale
    localStorage.setItem('laumeris-locale', locale)
    
    // Update document lang attribute
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }

  // Initialize locale
  const initialize = () => {
    const saved = localStorage.getItem('laumeris-locale') as LocaleCode | null
    
    if (saved && AVAILABLE_LOCALES.some(l => l.code === saved)) {
      currentLocale.value = saved
    } else {
      // Auto-detect from system
      currentLocale.value = detectSystemLocale()
    }
    
    // Update document lang attribute
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLocale.value
    }
  }

  return {
    currentLocale,
    currentLocaleOption,
    availableLocales: AVAILABLE_LOCALES,
    setLocale,
    initialize
  }
})

